import * as React from "react";
import { styled, Theme } from "@mui/material";
import { PatientColorMap } from "../layout/patientColorMap";
import { scrollableStyleByColor } from "fe-common/ui/commonStyles";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ReactComponent as BottomWaveIcon } from "../assets/waveLayout/bottomWave.svg";
import { ReactComponent as TopWaveIcon } from "../assets/waveLayout/topWave.svg";
import clsx from "clsx";
//region [[ Styles ]]
export enum WaveAreaAlignment {
  Top = "top",
  Bottom = "bottom",
}
export enum WaveAreaPosition {
  Relative = "relative",
  Absolute = "absolute",
  Fixed = "sticky",
  Static = "relative", // It has to be relative as the svg children are absolute
}

const WaveStylesView = styled("div")(
  ({
    color,
    position,
    zindex,
  }: {
    theme?: Theme;
    color?: string;
    position: WaveAreaPosition;
    zindex: number;
  }) => ({
    minHeight: "165px",
    background: "transparent",
    "& svg": {
      position: "absolute",
      left: 0,
      right: 0,
      zIndex: 0, // Keep it in background
      width: "100%",
      height: "auto", // Let it scale
    },
    "& svg path": {
      fill: color || PatientColorMap.White,
    },
    "&:not(.no-shadow) svg path": {
      filter: "drop-shadow(0px -3px 24px rgb(0 0 0 / 0.5))",
    },
    position: position || "absolute",
    display: "flex",
    left: 0,
    width: "100%",
    minWidth: "322px",
    zIndex: (zindex || 1) + 1,
    "& .content-wrapper": {
      background: color || PatientColorMap.White,
      position: "absolute",
      width: "100%",
      zIndex: zindex || 1,
      aspectRatio: "3.65", // Try and error until i got it - should match the wave icon
      // ...scrollableStyleByColor(color), // don't let elements break the aspect ratio
      height: "auto",
      // Just let the text flow over the wave, if needed,
      // or over the svg next to it
      overflow: "visible",
    },
    "& .content": {
      padding: "15px",
      display: "flex",
      flexFlow: "column",
      alignItems: "center",
      height: "100%",
      width: "100%",
    },
    // Align everything to top or bottom
    "&.top": {
      top: 0,
      "& svg": {
        top: 0,
      },
      "& .content-wrapper": {
        top: 0,
      },
    },
    "&.bottom": {
      // Give it responsive minimal height, so we can have
      // enough space for text
      minHeight: `min(49vw, 277px)`,
      bottom: 0,
      "& svg": {
        top: 0,
      },
      "& .content-wrapper": {
        bottom: 0,
        // Give text a responvie padding
        paddingRight: " min(3vw, 27px)",
        borderTopRightRadius: "84px",
      },
    },
  }),
);

export const WaveViewMainContent = styled("div")(
  ({
    gradient,
    color,
    backgroundimg,
    textcolor,
    top_wave,
    bottom_wave,
  }: {
    theme?: Theme;
    gradient: "true" | "false";
    color: string;
    backgroundimg?: string;
    textcolor?: string;
    top_wave?: string;
    bottom_wave?: string;
  }) => ({
    color: textcolor || "white",
    position: "relative",
    top: 0,
    left: 0,
    zIndex: 1,
    width: "100%",
    height: "fit-content",
    background:
      gradient === "true"
        ? `radial-gradient(83.53% 86.88% at 45.87% -11.43%, ${PatientColorMap.GradLight} 0%, ${PatientColorMap.GradDark} 100%);`
        : backgroundimg
        ? `url(${backgroundimg}) space space`
        : color,
    padding: "50px",
    paddingBottom: bottom_wave ? "165px" : "50px",
    paddingTop: top_wave ? "165px" : "58px",
    ...(scrollableStyleByColor(KahunColorMap.White) as any),
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]
export interface WaveAreaProps {
  color?: string;
  className?: string;
  children?: React.ReactNode | string;
  alignment: WaveAreaAlignment;
  position?: WaveAreaPosition;
  zIndex?: number;
  noShadow?: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
export const WaveArea = ({ ...props }: WaveAreaProps) => {
  return (
    <WaveStylesView
      className={clsx(
        props.className,
        props.alignment || "top",
        props.position,
        props.noShadow ? "no-shadow" : null,
      )}
      color={props.color}
      position={props.position || WaveAreaPosition.Absolute}
      zindex={props.zIndex}
    >
      {props.alignment === WaveAreaAlignment.Top ? (
        <TopWaveIcon />
      ) : (
        <BottomWaveIcon />
      )}
      <div className={"content-wrapper"}>
        <div className={"content"}>{props.children}</div>
      </div>
    </WaveStylesView>
  );
};

//endregion [[ Functions ]]
